// @file Define functions to make API calls
import getCsrfToken from '@@/bits/csrf_token'
import { captureFetchException } from '@@/bits/error_tracker'
import window, { browsingContextUid } from '@@/bits/global'
import { sleep } from '@@/bits/sleep'
import { getPathWithLeadingSlash } from '@@/bits/url'
import { viewAsPersonaForHeader } from '@@/surface/view_as_header_persona'
import { apiFetch } from '@padlet/arvo'
import type { FetchOptions } from '@padlet/fetch'
import { FetchError } from '@padlet/fetch'

const getApiFetchCommonOptions = (): FetchOptions => {
  const viewAs = viewAsPersonaForHeader()

  return {
    headers: {
      'X-CSRF-Token': getCsrfToken(),
      'X-UID': window?.ww?.uid || browsingContextUid,
      ...(viewAs && { 'X-View-As': viewAs }),
    },
  }
}

const apiFetchJson = async (url: string, options: FetchOptions = {}): Promise<any> => {
  const viewAs = viewAsPersonaForHeader()

  const commonOptions: FetchOptions = {
    headers: {
      Accept: 'application/json, application/vnd.api+json',
      'Content-Type': 'application/json; charset=utf-8',
      'X-CSRF-Token': getCsrfToken(),
      'X-UID': window?.ww?.uid || browsingContextUid,
      ...(viewAs && { 'X-View-As': viewAs }),
    },
  }

  // Merge `commonOptions.headers` with `options.headers` so callers don't have to provide
  // the common headers when they add custom headers.
  if (typeof options.headers === 'object') {
    options.headers = { ...commonOptions.headers, ...options.headers }
  }

  const pathWithLeadingSlash = getPathWithLeadingSlash(url)
  return await apiFetch<any>(pathWithLeadingSlash, commonOptions, options)
}

// status 0 = network failure (request never reached the server); status >= 500 = server error
const isRetryableError = (error: { status?: number }): boolean => {
  return error.status === 0 || (error.status != null && error.status >= 500)
}

// Retry gaps (not attempt counts). `sleep` picks a jittered delay in each [min, max] range.
const RETRY_DELAYS_IN_MS: Array<[number, number]> = [
  [300, 600],
  [1200, 2400],
  [3000, 6000],
]

const fetchJsonWithRetries = async (url: string, options: FetchOptions, source: string, maxTries = 4): Promise<any> => {
  let lastFailedError: FetchError | undefined

  for (let triesLeft = maxTries; triesLeft > 0; triesLeft -= 1) {
    try {
      const result = await apiFetchJson(url, options)
      if (lastFailedError) {
        captureFetchException(lastFailedError, { source, outcome: 'retry_succeeded' })
      }
      return result
    } catch (error) {
      lastFailedError = error
      if (triesLeft === 1 || !isRetryableError(error)) throw error
      const gapIndex = Math.min(maxTries - triesLeft, RETRY_DELAYS_IN_MS.length - 1)
      const [minDelay, maxDelay] = RETRY_DELAYS_IN_MS[gapIndex]
      await sleep(minDelay, maxDelay)
    }
  }
}

export { FetchError, apiFetchJson as fetchJson, fetchJsonWithRetries }
export { getApiFetchCommonOptions }
